/**
 * Free Product Manager
 * Automatically adds free product variants based on cart total thresholds
 * £50-£69.99 → variant 50802952110376 only
 * £70+ → BOTH variants 50802952110376 AND 50802950865192
 * 
 * IMPORTANT BEHAVIORS:
 * 1. Line Item Properties: Uses '_auto_free_gift': 'true' to mark auto-added gifts.
 *    This allows customers to manually purchase these products without interference.
 *    Only items with this property will be managed/removed by this system.
 * 
 * 2. Duplicate Prevention: If a variant already exists in cart (manually added or otherwise),
 *    the system will NOT add a duplicate. The Shopify discount will apply automatically
 *    to the existing item when the threshold is met.
 */

class FreeProductManager {
  constructor() {
    this.FREE_VARIANTS = {
      TIER_50: '50802952110376',  // £50 threshold
      TIER_70: '50802950865192'   // £70 threshold
    };
    
    this.THRESHOLDS = {
      TIER_50: 5000,  // £50 in pence
      TIER_70: 7000   // £70 in pence
    };

    this.isProcessing = false;
  }

  /**
   * Main method to check cart and manage free products
   * @param {boolean} refreshCart - Whether to refresh cart drawer after update
   * @returns {Promise}
   */
  async checkAndUpdateFreeProducts(refreshCart = true) {
    // DISABLED: Auto-adding free gifts is turned off
    // To re-enable, remove this early return
    console.log('[FreeProductManager] Auto-add free gifts is DISABLED');
    return;
    
    // Prevent concurrent executions
    if (this.isProcessing) {
      console.log('[FreeProductManager] Already processing, skipping...');
      return;
    }

    this.isProcessing = true;

    try {
      console.log('[FreeProductManager] Checking cart for free product eligibility...');
      
      // Get current cart state
      const cart = await this.getCart();
      
      // Determine which free products should be in cart
      const targetVariants = this.determineTargetVariants(cart.total_price);
      
      // Get current free products in cart (only auto-added ones)
      const currentFreeProducts = this.getCurrentFreeProducts(cart);
      
      // Check if we need to make changes
      const needsUpdate = this.needsUpdate(currentFreeProducts, targetVariants);
      
      if (!needsUpdate) {
        console.log('[FreeProductManager] No changes needed');
        this.isProcessing = false;
        return;
      }

      // Remove any free products that shouldn't be in cart (only auto-added ones)
      await this.removeIncorrectFreeProducts(currentFreeProducts, targetVariants);
      
      // Add any missing free products (but only if variant doesn't exist in cart at all)
      for (const variantId of targetVariants) {
        // Check if variant exists ANYWHERE in cart (not just auto-added)
        const variantExistsInCart = this.isVariantInCart(cart, variantId);
        
        if (variantExistsInCart) {
          console.log(`[FreeProductManager] Variant ${variantId} already in cart (manual or auto), skipping add. Discount will apply automatically.`);
          continue;
        }
        
        // Only add if not in cart at all
        await this.addFreeProduct(variantId);
      }

      // Refresh cart display if requested
      if (refreshCart) {
        await this.refreshCartDisplay();
      }

      console.log('[FreeProductManager] Successfully updated free products');
    } catch (error) {
      console.error('[FreeProductManager] Error:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Get current cart state
   * @returns {Promise<Object>}
   */
  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Determine which free product variants should be in cart based on total
   * @param {number} totalPrice - Cart total in pence
   * @returns {Array<string>} - Array of variant IDs that should be present
   */
  determineTargetVariants(totalPrice) {
    console.log(`[FreeProductManager] Cart total: £${(totalPrice / 100).toFixed(2)}`);
    
    const targetVariants = [];
    
    if (totalPrice >= this.THRESHOLDS.TIER_70) {
      console.log('[FreeProductManager] Eligible for BOTH £50 AND £70 tier gifts');
      targetVariants.push(this.FREE_VARIANTS.TIER_50);
      targetVariants.push(this.FREE_VARIANTS.TIER_70);
    } else if (totalPrice >= this.THRESHOLDS.TIER_50) {
      console.log('[FreeProductManager] Eligible for £50 tier gift only');
      targetVariants.push(this.FREE_VARIANTS.TIER_50);
    } else {
      console.log('[FreeProductManager] Not eligible for any free products');
    }
    
    return targetVariants;
  }

  /**
   * Get all free product line items currently in cart (only auto-added ones)
   * @param {Object} cart - Cart object
   * @returns {Array} - Array of {key, variant_id} objects
   */
  getCurrentFreeProducts(cart) {
    const freeVariantIds = Object.values(this.FREE_VARIANTS);
    return cart.items
      .filter(item => {
        // Only return items that are free variants AND have our auto-gift property
        const isFreeVariant = freeVariantIds.includes(item.variant_id.toString());
        const isAutoAdded = item.properties && item.properties._auto_free_gift === 'true';
        return isFreeVariant && isAutoAdded;
      })
      .map(item => ({
        key: item.key,
        variant_id: item.variant_id.toString(),
        line: cart.items.indexOf(item) + 1
      }));
  }

  /**
   * Check if a variant exists anywhere in cart (regardless of properties)
   * @param {Object} cart - Cart object
   * @param {string} variantId - Variant ID to check
   * @returns {boolean}
   */
  isVariantInCart(cart, variantId) {
    return cart.items.some(item => item.variant_id.toString() === variantId);
  }

  /**
   * Check if cart needs to be updated
   * @param {Array} currentFreeProducts - Current free products in cart
   * @param {Array<string>} targetVariants - Target variant IDs that should be present
   * @returns {boolean}
   */
  needsUpdate(currentFreeProducts, targetVariants) {
    // Get current variant IDs
    const currentVariantIds = currentFreeProducts.map(p => p.variant_id);
    
    // Check if any target variants are missing
    const missingVariants = targetVariants.filter(id => !currentVariantIds.includes(id));
    
    // Check if any current variants shouldn't be there
    const extraVariants = currentVariantIds.filter(id => !targetVariants.includes(id));
    
    return missingVariants.length > 0 || extraVariants.length > 0;
  }

  /**
   * Remove free products that shouldn't be in cart
   * @param {Array} currentFreeProducts - Current free products
   * @param {Array<string>} targetVariants - Variants that should remain
   * @returns {Promise}
   */
  async removeIncorrectFreeProducts(currentFreeProducts, targetVariants) {
    const productsToRemove = currentFreeProducts.filter(p => 
      !targetVariants.includes(p.variant_id)
    );

    if (productsToRemove.length === 0) {
      return;
    }

    console.log(`[FreeProductManager] Removing ${productsToRemove.length} incorrect free product(s)`);

    for (const product of productsToRemove) {
      try {
        await this.removeCartItem(product.key);
        console.log(`[FreeProductManager] Removed variant ${product.variant_id}`);
      } catch (error) {
        console.error(`[FreeProductManager] Failed to remove variant ${product.variant_id}:`, error);
      }
    }
  }

  /**
   * Remove an item from cart by key
   * @param {string} key - Cart item key
   * @returns {Promise}
   */
  async removeCartItem(key) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: key,
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove item: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Add free product to cart with property marker
   * @param {string} variantId - Variant ID to add
   * @returns {Promise}
   */
  async addFreeProduct(variantId) {
    console.log(`[FreeProductManager] Adding free product variant ${variantId} with auto-gift marker`);
    
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: {
            '_auto_free_gift': 'true'  // Hidden property to mark auto-added items
          }
        }]
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to add free product: ${response.status}`);
    }

    console.log(`[FreeProductManager] Successfully added variant ${variantId} as auto-gift`);
    return response.json();
  }

  /**
   * Refresh cart drawer display
   * @returns {Promise}
   */
  async refreshCartDisplay() {
    const cartDrawer = document.querySelector('cart-drawer');
    const countBubble = document.querySelector('.count-bubble');

    if (!cartDrawer) {
      console.log('[FreeProductManager] Cart drawer not found, skipping refresh');
      return;
    }

    try {
      // Update cart count
      const cart = await this.getCart();
      
      if (countBubble) {
        countBubble.textContent = cart.item_count;
        countBubble.style.display = cart.item_count > 0 ? 'flex' : 'none';
      }

      // Fetch and update cart drawer HTML
      const cartPageResponse = await fetch('/cart');
      const cartHtml = await cartPageResponse.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(cartHtml, 'text/html');
      const newCartDrawer = doc.querySelector('cart-drawer');

      if (newCartDrawer) {
        cartDrawer.innerHTML = newCartDrawer.innerHTML;
        
        // Reinitialize sliding messages if available
        if (typeof initSlidingMessages === 'function') {
          initSlidingMessages();
        }

        // Reinitialize discount manager if available
        setTimeout(() => {
          if (window.CartDiscountManager) {
            window.CartDiscountManager.init();
            window.CartDiscountManager.refreshUI();
          }
        }, 300);
      }

      console.log('[FreeProductManager] Cart display refreshed');
    } catch (error) {
      console.error('[FreeProductManager] Failed to refresh cart display:', error);
    }
  }

  /**
   * Refresh cart items section (for main cart page)
   * @returns {Promise}
   */
  async refreshCartItems() {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      // Reinitialize discount manager if available
      if (window.CartDiscountManager) {
        window.CartDiscountManager.init();
        window.CartDiscountManager.refreshUI();
      }
    } catch (error) {
      console.error('[FreeProductManager] Failed to refresh cart items:', error);
    }
  }
}

// Initialize global instance
window.FreeProductManager = new FreeProductManager();

console.log('[FreeProductManager] Initialized and ready');
